import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { F0Button } from '@factorialco/f0-react'
import {
  PerformancePeerAssignmentsReviewEvaluationFragment,
  PerformancePeerAssignmentsTargetFragment,
  PerformanceReviewOverviewPeerAssignmentTaskDocument,
  RemovePeersPerformanceReviewProcessTargetDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

interface Props {
  processTargetId: number
  processTargetAccess: PerformancePeerAssignmentsTargetFragment
  currentPeerEvaluations: PerformancePeerAssignmentsReviewEvaluationFragment[]
  currentPeerEmployeeIds: number[]
  onClose: () => void
}

const RemovePeersModal = ({
  processTargetId,
  processTargetAccess,
  currentPeerEvaluations,
  currentPeerEmployeeIds,
  onClose,
}: Props) => {
  const { reviewId } = useParams()
  const [selectedEmployeesIds, setSelectedEmployeesIds] = useState<number[]>([])

  const [removePeersToPerformanceProcess, { loading: adding }] = useMutation(
    RemovePeersPerformanceReviewProcessTargetDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.removePeerEvaluationsPerformanceReviewProcessTarget,
      refetchQueries: [PerformanceReviewOverviewPeerAssignmentTaskDocument],
    }
  )

  const submit = async () => {
    const response = await removePeersToPerformanceProcess({
      variables: {
        evaluationIds: currentPeerEvaluations
          .filter(
            (e) =>
              e.reviewerEmployeeName?.id &&
              selectedEmployeesIds.some((id) => id === e.reviewerEmployeeName?.id)
          )
          .map((ev) => ev.id),
        id: `${reviewId}-${processTargetId}`,
      },
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    onClose()
  }

  return (
    <Modal
      title={i18n.t('performance_process.reviews.peer_management.remove_peers_for', {
        target_name: processTargetAccess.employeeName?.fullName,
      })}
      illustration="user-male-female"
      onClose={onClose}
      footer={
        <F0Button
          disabled={!selectedEmployeesIds.length || adding}
          label={i18n.t('performance_process.reviews.peer_management.remove_peers')}
          onClick={submit}
        />
      }
    >
      <EmployeeSelectorV2
        multiple
        forcedFilters={{
          excludeIds: [processTargetId],
          employeeIds: currentPeerEmployeeIds,
        }}
        options={{
          asList: true,
          disableSelectAll: true,
          hideLabel: true,
        }}
        onChange={(state) => {
          if (state.type === 'manual') {
            setSelectedEmployeesIds(state.ids)
          }
        }}
      />
    </Modal>
  )
}

export default observer(RemovePeersModal)
